/**
 * useRemoteConfigurations Hook
 *
 * Custom hook for managing remote configurations data and CRUD operations.
 * Handles loading, creating, updating, and deleting configurations.
 */

var React = require('react');
var useState = React.useState;
var useEffect = React.useEffect;
var useCallback = React.useCallback;
var remoteConfigurationsManager = require('@managers/remoteConfigurationsManager');

/**
 * Transform server configuration format to frontend format
 * Server returns objects like { "stg": true }, frontend expects arrays like ["stg :True"]
 * @param {Object} config - Raw configuration from server
 * @returns {Object} Transformed configuration
 */
var transformConfiguration = function (config) {
  var transformed = Object.assign({}, config);

  // Convert environments object to array
  if (
    config.environments &&
    config.environments !== null &&
    typeof config.environments === 'object' &&
    !Array.isArray(config.environments)
  ) {
    try {
      transformed.environments = Object.keys(config.environments).map(
        function (key) {
          return key + ' :' + (config.environments[key] ? 'True' : 'False');
        },
      );
    } catch (e) {
      console.error('Failed to transform environments:', e);
      transformed.environments = [];
    }
  }

  // Convert distributions object to array
  if (
    config.distributions &&
    config.distributions !== null &&
    typeof config.distributions === 'object' &&
    !Array.isArray(config.distributions)
  ) {
    try {
      transformed.distributions = Object.keys(config.distributions).map(
        function (key) {
          return key + ' :' + (config.distributions[key] ? 'True' : 'False');
        },
      );
    } catch (e) {
      console.error('Failed to transform distributions:', e);
      transformed.distributions = [];
    }
  }

  // Convert farming_units object to array
  if (
    config.farming_units &&
    config.farming_units !== null &&
    typeof config.farming_units === 'object' &&
    !Array.isArray(config.farming_units)
  ) {
    try {
      transformed.farming_units = Object.keys(config.farming_units).map(
        function (key) {
          return key + ' :' + (config.farming_units[key] ? 'True' : 'False');
        },
      );
    } catch (e) {
      console.error('Failed to transform farming_units:', e);
      transformed.farming_units = [];
    }
  }

  // Convert users_email object to array (just the keys/emails)
  if (
    config.users_email &&
    config.users_email !== null &&
    typeof config.users_email === 'object' &&
    !Array.isArray(config.users_email)
  ) {
    try {
      transformed.users_email = Object.keys(config.users_email);
    } catch (e) {
      console.error('Failed to transform users_email:', e);
      transformed.users_email = [];
    }
  }

  return transformed;
};

/**
 * Custom hook for remote configurations management
 * @returns {Object} Hook state and methods
 */
var useRemoteConfigurations = function () {
  var configurationsState = useState([]);
  var configurations = configurationsState[0];
  var setConfigurations = configurationsState[1];

  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  var errorState = useState(null);
  var error = errorState[0];
  var setError = errorState[1];

  /**
   * Fetch all configurations from API
   */
  var fetchConfigurations = useCallback(function () {
    setLoading(true);
    setError(null);

    remoteConfigurationsManager
      .get_configurations()
      .then(function (res) {
        if (res && res.data && res.data.configurations) {
          // Transform each configuration to frontend format
          var transformedConfigs = res.data.configurations.map(
            transformConfiguration,
          );
          setConfigurations(transformedConfigs);
        } else {
          setConfigurations([]);
        }
        setLoading(false);
      })
      .catch(function (err) {
        console.error('Failed to fetch configurations:', err);
        setError('Failed to load configurations. Please try again.');
        setLoading(false);
      });
  }, []);

  /**
   * Create a new configuration
   * @param {Object} configuration - Configuration data
   * @returns {Promise} Resolves with created config or rejects with error
   */
  var createConfiguration = useCallback(function (configuration) {
    return new Promise(function (resolve, reject) {
      // Prepare configuration data
      var preparedConfig = Object.assign({}, configuration);
      preparedConfig.default_value = preparedConfig.default_value.toString();

      if (typeof preparedConfig.users_email === 'string') {
        preparedConfig.users_email =
          preparedConfig.users_email.length > 0
            ? preparedConfig.users_email.split(',').map(function (e) {
                return e.trim();
              })
            : [];
      }

      preparedConfig.default_value =
        preparedConfig.default_value.toLowerCase() === 'true';

      remoteConfigurationsManager
        .create_new_configuration(preparedConfig)
        .then(function (res) {
          // Build the new configuration object for local state
          var newConfig = Object.assign({}, preparedConfig);
          newConfig.id = res.configuration_id;
          newConfig.distributions =
            remoteConfigurationsManager.convertObjectToArray(
              preparedConfig.distributions || {},
            );
          newConfig.farming_units =
            remoteConfigurationsManager.convertObjectToArray(
              preparedConfig.farming_units || {},
            );
          newConfig.environments =
            remoteConfigurationsManager.convertObjectToArray(
              preparedConfig.environments || {},
            );
          newConfig.users_email =
            remoteConfigurationsManager.convertObjectToArray(
              preparedConfig.users_email || {},
            );
          newConfig.extra_data = JSON.stringify(
            preparedConfig.extra_data || {},
          );

          setConfigurations(function (prev) {
            return prev.concat([newConfig]);
          });

          resolve(newConfig);
        })
        .catch(function (err) {
          console.error('Failed to create configuration:', err);
          reject(err);
        });
    });
  }, []);

  /**
   * Update an existing configuration
   * @param {Object} configuration - Configuration data with id
   * @returns {Promise} Resolves with updated config or rejects with error
   */
  var updateConfiguration = useCallback(function (configuration) {
    return new Promise(function (resolve, reject) {
      // Prepare configuration data
      var preparedConfig = Object.assign({}, configuration);

      if (typeof preparedConfig.users_email === 'string') {
        preparedConfig.users_email = preparedConfig.users_email
          .split(',')
          .map(function (e) {
            return e.trim();
          });
      }

      if (typeof preparedConfig.default_value === 'string') {
        preparedConfig.default_value =
          preparedConfig.default_value.toLowerCase() === 'true';
      }

      remoteConfigurationsManager
        .update_configuration(preparedConfig)
        .then(function () {
          // Build the updated configuration object for local state
          var updatedConfig = Object.assign({}, preparedConfig);
          updatedConfig.distributions =
            remoteConfigurationsManager.convertObjectToArray(
              preparedConfig.distributions || {},
            );
          updatedConfig.farming_units =
            remoteConfigurationsManager.convertObjectToArray(
              preparedConfig.farming_units || {},
            );
          updatedConfig.environments =
            remoteConfigurationsManager.convertObjectToArray(
              preparedConfig.environments || {},
            );
          updatedConfig.users_email =
            remoteConfigurationsManager.convertObjectToArray(
              preparedConfig.users_email || {},
            );
          updatedConfig.extra_data = JSON.stringify(
            preparedConfig.extra_data || {},
          );

          setConfigurations(function (prev) {
            return prev.map(function (config) {
              if (config.id === updatedConfig.id) {
                return updatedConfig;
              }
              return config;
            });
          });

          resolve(updatedConfig);
        })
        .catch(function (err) {
          console.error('Failed to update configuration:', err);
          reject(err);
        });
    });
  }, []);

  /**
   * Delete a configuration
   * @param {string} configurationId - ID of configuration to delete
   * @returns {Promise} Resolves on success or rejects with error
   */
  var deleteConfiguration = useCallback(function (configurationId) {
    return new Promise(function (resolve, reject) {
      remoteConfigurationsManager
        .delete_configuration(configurationId)
        .then(function () {
          setConfigurations(function (prev) {
            return prev.filter(function (config) {
              return config.id !== configurationId;
            });
          });
          resolve();
        })
        .catch(function (err) {
          console.error('Failed to delete configuration:', err);
          reject(err);
        });
    });
  }, []);

  /**
   * Toggle the default_value of a configuration
   * @param {Object} configuration - Configuration to toggle
   * @returns {Promise} Resolves with updated config or rejects with error
   */
  var toggleConfiguration = useCallback(
    function (configuration) {
      var updatedConfig = Object.assign({}, configuration);
      // Handle both boolean and string values
      var currentlyEnabled =
        configuration.default_value === true ||
        configuration.default_value === 'true';
      updatedConfig.default_value = !currentlyEnabled;

      // Store previous value for accurate rollback
      var previousValue = configuration.default_value;

      // Optimistically update UI
      setConfigurations(function (prev) {
        return prev.map(function (config) {
          if (config.id === configuration.id) {
            return Object.assign({}, config, {
              default_value: updatedConfig.default_value,
            });
          }
          return config;
        });
      });

      return updateConfiguration(updatedConfig).catch(function (err) {
        // Rollback to previous value
        setConfigurations(function (prev) {
          return prev.map(function (config) {
            if (config.id === configuration.id) {
              return Object.assign({}, config, {
                default_value: previousValue,
              });
            }
            return config;
          });
        });
        throw err;
      });
    },
    [updateConfiguration],
  );

  // Initial fetch on mount
  useEffect(
    function () {
      fetchConfigurations();
    },
    [fetchConfigurations],
  );

  return {
    configurations: configurations,
    loading: loading,
    error: error,
    refresh: fetchConfigurations,
    createConfiguration: createConfiguration,
    updateConfiguration: updateConfiguration,
    deleteConfiguration: deleteConfiguration,
    toggleConfiguration: toggleConfiguration,
  };
};

module.exports = useRemoteConfigurations;
